/**
 * Apollo Client Accessor - Class Pattern with Dynamic Method Forwarding
 *
 * Uses a class that automatically forwards Apollo client methods
 * to break circular dependencies while maintaining the original API.
 */

/**
 * A wrapper class that handles Apollo client access through method forwarding
 */
class ApolloClientAccessor {
  constructor() {
    this.clientInstance = null;

    // Define allowed properties for faster lookup
    this.accessorProps = new Set(["getClient", "setClient", "clientInstance"]);

    // Cache for bound methods
    this.boundMethodsCache = new Map();

    // Create a proxy that will automatically forward method calls to the client
    return new Proxy(this, {
      get: (target, prop) => {
        // Check if the property is explicitly allowed
        if (target.accessorProps.has(prop)) {
          return target[prop];
        }

        // If client is not initialized, throw error only when attempting to use client methods
        if (!target.clientInstance) {
          throw new Error("Apollo client accessed before initialization");
        }

        // Forward method calls and properties to the client instance
        const value = target.clientInstance[prop];

        // Bind methods to the client instance
        if (typeof value === "function") {
          if (!target.boundMethodsCache.has(prop)) {
            target.boundMethodsCache.set(
              prop,
              value.bind(target.clientInstance)
            );
          }
          return target.boundMethodsCache.get(prop);
        }

        // Return properties directly
        return value;
      },
    });
  }

  /**
   * Sets the Apollo client instance
   * @param {object} client - The Apollo client instance
   * @returns {object} The Apollo client instance
   */
  setClient(client) {
    this.clientInstance = client;
    return this.clientInstance;
  }

  /**
   * Returns the Apollo client instance
   * @returns {object} The Apollo client instance
   */
  getClient() {
    if (!this.clientInstance) {
      console.warn("Apollo client accessed before initialization");
    }
    return this.clientInstance;
  }
}

// Create a singleton instance
const clientAccessor = new ApolloClientAccessor();

// Export getter/setter functions for direct access

export const setApolloClient = client => clientAccessor.setClient(client);

// Export the accessor as default for import as 'client'
export default clientAccessor;
